import { Typography } from "@mui/material";
import type { DeliveryType } from "@types_def/models/delivery.types";
import { getDeliveryTypeColor } from "@utils/functions/Delivery/DeliveryType/deliveryTypeAttributes";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { enumToStr } from "@utils/strEnum";
import React, { useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { calendarLocaleEnUS, calendarLocalizerFunctions, toPickerDate } from "@/utils/picker-date";

const locales = {
  "en-US": calendarLocaleEnUS,
};

// Events and ranges are picker dates (Paris wall-clock), see @/utils/picker-date.
const localizer = dateFnsLocalizer({
  ...calendarLocalizerFunctions,
  locales,
});

type Event = {
  id: number;
  title: string | number;
  start: Date;
  end: Date;
  type: DeliveryType;
  address: string;
};

type TClientCalendar = {
  handleRangeChange: (range: any) => void;
  data: Event[];
};

function CustomEvent({ event }: { event: Event }) {
  const { t } = useTranslation();
  return (
    <Typography variant="body2">
      <span style={{ textTransform: "capitalize" }}>{t("calendar.mission")}</span> N°
      {event.id}
      <br />
      {event.address}
    </Typography>
  );
}

function ClientCalendar({ data, handleRangeChange }: TClientCalendar) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState<"month" | "week" | "day" | "agenda" | "work_week">("month");
  const [currentDate, setCurrentDate] = useState(() => toPickerDate(new Date()) ?? new Date());

  const eventStyleGetter = React.useCallback(
    (_event: Event) => ({
      style: {
        backgroundColor: getDeliveryTypeColor(_event.type),
        borderRadius: "4px",
        color: "white",
      },
    }),
    []
  );

  const components = React.useMemo(() => ({ event: CustomEvent }), []);

  const handleSelectEvent = React.useCallback(
    (event: Event) => {
      const strType = enumToStr<DeliveryType>(event.type);
      void navigate(`/client/pilot/standard/${strType}/${event.id}`);
    },
    [navigate]
  );

  const messages = React.useMemo(
    () => ({
      next: t("common.navigation.next"),
      previous: t("common.navigation.previous"),
      today: t("calendar.today"),
      month: t("calendar.month"),
      week: t("calendar.week"),
      day: t("calendar.day"),
      agenda: t("calendar.agenda"),
      date: t("calendar.date"),
      time: t("calendar.time"),
      event: t("calendar.event"),
      noEventsInRange: t("calendar.noEventsInRange"),
      showMore: (total: number) => `+ ${total} ${t("calendar.showMore")}`,
    }),
    [t]
  );

  const handleNavigate = React.useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, []);

  return (
    <Calendar
      popup
      date={currentDate}
      view={view}
      onView={setView}
      onNavigate={handleNavigate}
      eventPropGetter={eventStyleGetter}
      style={{ height: 600 }}
      localizer={localizer}
      events={data}
      startAccessor="start"
      endAccessor="end"
      onSelectEvent={handleSelectEvent}
      onRangeChange={handleRangeChange}
      components={components}
      messages={messages}
    />
  );
}

export default ClientCalendar;
